import type { RenderPropArgs } from '@rover/react-lib/src/components/utils/Form';
import type { FormChangesProps } from '@rover/react-lib/src/pages/provider-profile/types/Form';
import type { Radius } from '@rover/react-lib/src/pages/provider-profile/types/hub/Radius';
import type { ValidationErrors } from '@rover/react-lib/src/pages/provider-profile/types/ValidationError';
import type { ServiceAreaIsochrone } from '@rover/rsdk/src/apiClient/latest/model/serviceAreaIsochrone';
import type { ServiceAreaPreferences } from '@rover/rsdk/src/apiClient/latest/model/serviceAreaPreferences';
import type { SimplifiedArea } from '@rover/rsdk/src/apiClient/latest/model/simplifiedArea';
import type { Address } from '@rover/types/src/Address';
import type { AvailabilityPreferences } from '@rover/types/src/AvailabilityPreferences';
import type { FormObject, FormValues } from '@rover/types/src/Form';
import type { HostingServicePreferences } from '@rover/types/src/HostingServicePreferences';
import type { PetPreferences } from '@rover/types/src/PetPreferences';
import type { ServiceAddOn } from '@rover/types/src/ServiceAddOn';
import type { TrainingServicePreferences } from '@rover/types/src/TrainingServicePreferences';
import type { TravelingServicePreferences } from '@rover/types/src/TravelingServicePreferences';

export type NoneOptions = {
  ownersExpectNone?: boolean | null | undefined;
  dayTimeAvailabilityNone?: boolean | null | undefined;
  hostingOptionsNone?: boolean | null | undefined;
};
export type ServiceFormInitialDataBase = NoneOptions & {
  active: boolean;
  autoUpdateAdditionalRates: boolean;
  availabilityPreferences: AvailabilityPreferences;
  cancellationPolicy: string;
  isAway: boolean;
  isAcceptingNewCustomers: boolean;
  isAcceptingNewRecurringClients: boolean;
  petPreferences: PetPreferences;
  serviceAddons: ServiceAddOn[];
  shouldDisplaySharedPreferencesWarning: boolean;
  dogCareEnabled?: boolean;
  referenceRateSlug?: string;
};
export type TravelingServiceFormData = ServiceFormInitialDataBase & {
  preferences: TravelingServicePreferences | ServiceAreaPreferences;
};
export type HostingServiceFormData = ServiceFormInitialDataBase & {
  preferences: HostingServicePreferences;
};
export type WalkingServiceFormData = ServiceFormInitialDataBase & {
  preferences: TravelingServicePreferences | ServiceAreaPreferences;
};
export type TrainingServiceFormData = ServiceFormInitialDataBase & {
  preferences: TrainingServicePreferences;
};
export type ServiceFormData =
  | TravelingServiceFormData
  | HostingServiceFormData
  | WalkingServiceFormData
  | TrainingServiceFormData;

// The legacy `Form` component constrains its value type to `FormValues`, which requires a
// string index signature. Some `preferences` shapes are Orval-generated *interfaces*
// (e.g. `ServiceAreaPreferences`), and interfaces never satisfy an index signature in
// TypeScript. Intersecting with `FormValues` supplies that index signature while preserving
// every concrete field type, so `form.data.<field>` stays precisely typed.
export type TravelingServiceFormAsFormValues = TravelingServiceFormData & FormValues;
export type HostingServiceFormAsFormValues = HostingServiceFormData & FormValues;
export type WalkingServiceFormAsFormValues = WalkingServiceFormData & FormValues;
export type TrainingServiceFormAsFormValues = TrainingServiceFormData & FormValues;
export type ServiceFormValidationErrors = {
  // The availability validators (`dayOfTheWeekAvailabilityValidator`,
  // `fullTimeAvailabilityValidator`, `nonBoardingSpacesAvailableValidator`) each resolve to a
  // scalar `string | null` error message, and every consuming field component types its `error`
  // prop as `string | null | undefined`. (`serviceAddons`, below, is the exception — those
  // validators return `ValidationErrors` arrays that are read via `getErrorFromArray`.)
  availabilityPreferences: {
    dayOfTheWeekAvailabilityError: string | null | undefined;
    dayTimeAvailabilityError?: string | null | undefined;
    fullTimeAvailabilityError?: string | null | undefined;
    spacesAvailable: string | null | undefined;
  };
  cancellationPolicy: string | null | undefined;
  petPreferences: {
    dogSizeError: string | null | undefined;
    puppies: string | null | undefined;
  };
  serviceAddons: {
    priceValidatorErrors: ValidationErrors;
    durationValidatorErrors: ValidationErrors;
    recommendedRatesValidatorErrors: ValidationErrors;
  };
  preferences: {
    timeAlone: string | null | undefined;
    homeTypeError: string | null | undefined;
    hostingOptionsTypeError: string | null | undefined;
    ownersExpectTypeError: string | null | undefined;
    radiusInMiles: string | null | undefined;
    yardTypeError: string | null | undefined;
  };
};
// The generic `FormErrors<FV, FVD>` produced by `RenderPropArgs` collapses every field to
// `never` whenever the validators are supplied through the default (all-optional) `FVD` — the
// `FVD[K] extends undefined` guard fails for `X | undefined`, and no later branch matches, so
// each key resolves to `never`. That makes `form.validationErrors.<field>` unusable. In
// practice these service forms build a concrete, well-known error object, so we override
// `validationErrors` with that precise shape. `FormErrors`' all-`never` object is assignable to
// `ServiceFormValidationErrors`, so the render-prop `form` still flows into these aliases.
type WithServiceFormValidationErrors<FV extends FormValues> = Omit<
  RenderPropArgs<FV>,
  'validationErrors'
> & { validationErrors: ServiceFormValidationErrors };

export type TravelingForm = WithServiceFormValidationErrors<TravelingServiceFormAsFormValues>;
export type HostingForm = WithServiceFormValidationErrors<HostingServiceFormAsFormValues>;
export type WalkingForm = WithServiceFormValidationErrors<WalkingServiceFormAsFormValues>;
export type TrainingForm = WithServiceFormValidationErrors<TrainingServiceFormAsFormValues>;

// `ServiceFormArea` is shared by the traveling/walking and training service forms. Each caller
// passes a differently-shaped `preferences` object (legacy radius+location fields vs. the newer
// service-area isochrone fields), and which fields are populated is chosen at runtime by the
// `shouldShowServiceAreaPreferences` flag. This combined view models the superset of fields the
// component reads and writes, so the component sees a single precise type rather than an
// unnarrowable union (which would also collapse `onChange`'s parameter to an unsatisfiable
// intersection). Callers cast their concrete form to this shape at the boundary.
export type ServiceAreaFormPreferences = {
  routingProfile: ServiceAreaIsochrone['routingProfile'];
  contourType: ServiceAreaIsochrone['contourType'];
  contourValue: ServiceAreaIsochrone['contourValue'];
  area?: FormObject | null;
  simplifiedArea?: (SimplifiedArea & FormObject) | null;
  serviceLocation?: Address | null;
  travelingServiceLocation?: Address | null;
  radiusObj?: Radius | null;
  timeAlone?: string | null;
  longStays?: boolean | null;
};
export type ServiceAreaFormValues = ServiceFormInitialDataBase & {
  preferences: ServiceAreaFormPreferences;
} & FormValues;
export type ServiceAreaForm = WithServiceFormValidationErrors<ServiceAreaFormValues>;

/**
 * In here to allow both Hosting and TravelingServiceForm to access the same base type
 */
export type ServiceFormPropsBase = NoneOptions &
  FormChangesProps & {
    onSubmit: (d: ServiceFormData) => void;
    toggleIsDisplayingAdditionalAddOns: (v: boolean) => void;
    showAdditionalRatesModal: boolean;
    setShowAdditionalRatesModal: (v: boolean) => void;
  };

export enum ReturnQueryParam {
  CatExperienceAdded = 'catExperienceAdded',
  ExperienceAdded = 'experienceAdded',
}
